import React, { useContext, useEffect } from "react"
import { observer } from "mobx-react"
import { Loader } from "@mantine/core"
import { AppWorkspaceContext } from "../stores/AppWorkspace"
import { SITE_PAGES } from "../helpers/constants"
import { SiteHeader } from "./SiteHeader"
import { SiteFooter } from "./SiteFooter"
import { DocsSpotlight } from "./DocsSpotlight"
import { HomePage } from "./pages/HomePage"
import { ApiDocsPage } from "./pages/ApiDocsPage"
import { GuidePage } from "./pages/GuidePage"
import { IntegrationPage } from "./integrations/IntegrationPage"
import { PlatformsPage } from "./platforms/PlatformsPage"
import { ProcessorsPage } from "./processors/ProcessorsPage"
import { commitAnalyticsPageview } from "../../analytics"
import { apiEntriesChunk, guidesChunk } from "../../docs/lazy"

export const AppWorkspaceContainer: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)

  useEffect(() => {
    workspace.hydrateDataFromPage()
  }, [workspace])

  useEffect(() => {
    if (!workspace.hydrated || workspace.titlePending) return
    document.title = workspace.pageTitle
    // This is the route-commit boundary: the observable route and its title
    // have both reached the DOM. Capturing inside pushState/popstate runs too
    // early and attributes the previous page's title to the new pathname.
    commitAnalyticsPageview()
  }, [workspace.currentPath, workspace.hydrated, workspace.pageTitle, workspace.titlePending])

  // A #hash into a guide or the API reference names an element that exists
  // only once that chunk has landed, so the scroll below runs again then.
  const hashTargetsLoaded = workspace.hash ? `${guidesChunk.loaded}:${apiEntriesChunk.loaded}` : ""

  useEffect(() => {
    if (!workspace.hydrated) return
    const hash = workspace.hash
    if (!hash) {
      window.scrollTo({ top: 0 })
      return
    }
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 40)
    return () => window.clearTimeout(timer)
  }, [
    workspace.hydrated,
    workspace.openPage,
    workspace.hash,
    workspace.guides.slug,
    workspace.frameworkId,
    workspace.platformId,
    workspace.processorId,
    hashTargetsLoaded
  ])

  if (!workspace.hydrated) {
    return (
      <div className="or-loader">
        <Loader color="orGreen" />
      </div>
    )
  }

  return (
    <div className="or-shell">
      <SiteHeader />
      <DocsSpotlight />
      <main className="or-main">
        {workspace.openPage === SITE_PAGES.HOME && <HomePage />}
        {workspace.openPage === SITE_PAGES.API_DOCS && <ApiDocsPage />}
        {workspace.openPage === SITE_PAGES.GUIDES && <GuidePage />}
        {workspace.openPage === SITE_PAGES.INTEGRATION && <IntegrationPage />}
        {workspace.openPage === SITE_PAGES.PLATFORMS && <PlatformsPage />}
        {workspace.openPage === SITE_PAGES.PROCESSORS && <ProcessorsPage />}
      </main>
      <SiteFooter />
    </div>
  )
})
